import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../shared/components/form/input"
import useMyPermission from '../../hooks/useMyPermission'
import ListGeneratorRow from './ListRow/ListGeneratorRow'
import Modal from '../../shared/modal/Modal'
import NewGenerator from './NewGenerator'
import GeneratorFilterModal from './GeneratorFilterModal'
import Button from '../../shared/components/Button/Button'
import './Generator.css'

const CONSUMABLE_FILTER_KEYS = ['primaryAir', 'secundaryAir', 'primaryComb', 'secundaryComb', 'waterFilter', 'lubrificant', 'oil', 'racor', 'hose', 'straps']
const TEXT_FILTER_KEYS = ['manufacturer', 'engine', 'power', 'panel', 'injectionSystem']

const EMPTY_FILTERS = {
    status: 'all',
    linked: 'all',
    consumableField: 'all',
    consumableSearch: '',
    ...Object.fromEntries(TEXT_FILTER_KEYS.map(key => [key, '']))
}

function Generator({ selectItem, handleOnSelectItem, selectedData, onFinishSelect }) {

    const [token] = useState(localStorage.getItem('token') || '')
    const [generators, setGenerators] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [load, setLoad] = useState(false)
    const [selectedCount, setSelectedCount] = useState((selectedData || []).length)

    const handleSelectItem = (next) => {
        setSelectedCount(next.length)
        handleOnSelectItem(next)
    }

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const myPermissionNew  = checkPermission(myPermission, "generator_Create")
    const myPermissionView = checkPermission(myPermission, "generator_View")

    const getAll = () => {
        api.get(`/generator/getall`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setGenerators(response.data.generators)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const filter = useMemo(() => {
        return generators.filter((item) => {
            const matchSearch = item.name.toLowerCase().includes(search.toLowerCase())
            const matchStatus =
                filters.status === 'all'      ? true :
                filters.status === 'active'   ? !item.disabled :
                                                 !!item.disabled
            const matchLinked =
                filters.linked === 'all'      ? true :
                filters.linked === 'linked'   ? !!item.linked :
                                                 !item.linked
            const matchTextFilters    = TEXT_FILTER_KEYS.every(key => !filters[key] || item[key]?.toLowerCase().includes(filters[key].toLowerCase()))
            const consumableKeys      = filters.consumableField === 'all' ? CONSUMABLE_FILTER_KEYS : [filters.consumableField]
            const matchConsumable     = !filters.consumableSearch || consumableKeys.some(key => item[key]?.toLowerCase().includes(filters.consumableSearch.toLowerCase()))
            return matchSearch && matchStatus && matchLinked && matchTextFilters && matchConsumable
        })
    }, [generators, search, filters])

    const hasActiveFilter = !!search || filters.status !== 'all' || filters.linked !== 'all' || filters.consumableSearch || TEXT_FILTER_KEYS.some(key => filters[key])

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando geradores...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Geradores</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                            {selectItem && onFinishSelect && (
                                <>
                                    <Button variant="finish" onClick={onFinishSelect}>Concluir</Button>
                                    <span className="pg-count" style={{ marginLeft: '24px' }}>
                                        {selectedCount} {selectedCount === 1 ? 'gerador selecionado' : 'geradores selecionados'}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar gerador..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'gerador' : 'geradores'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'gerador encontrado' : 'geradores encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListGeneratorRow
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermission={myPermission}
                        handleOnSelectItem={handleSelectItem}
                        selectItem={selectItem}
                        selectedData={selectedData}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum gerador encontrado</p>
                        <p className="pg-empty-subtitle">
                            {generators.length === 0
                                ? 'Cadastre o primeiro gerador para começar.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={80}
                content={
                    <NewGenerator
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

            {modalFilter && (
                <GeneratorFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar geradores</p>
        </div>
    )
}

export default Generator
